import React, { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { MessageProps } from '../types';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import aimdocLogo from '../logo_symbol.png';
import rehypeRaw from "rehype-raw";
import Calendly from './actions/Calendly';
import YouTube from './actions/YouTube';
import TidyCal from './actions/TidyCal';
import ChiliPiper from './actions/ChiliPiper';
import Form from './actions/Form';
import * as HeroIconSolid from '@heroicons/react/24/solid'
import { Transition } from '@headlessui/react';
import Actions from './actions/Actions';
import { hexToRgb } from './utils/agentUtils';

interface Component {
    type: string;
    [key: string]: any;
}

type ComponentMapping = {
    calendly: typeof Calendly;
    tidycal: typeof TidyCal;
    chili_piper: typeof ChiliPiper;
    youtube: typeof YouTube;
    form_field: typeof Form;
    form: typeof Form;
};

interface MemoizedComponentProps {
    component: Component;
    config: any;
    conversationId: string;
    sendMessage: (message: string, type?: string) => void;
    formFields?: Record<string, any>;
    onFormFieldUpdate?: (fieldName: string, value: string) => void;
    sidebar?: boolean;
    gettingFullResponse: boolean;
    isLastFormMessage?: boolean;
    setLeadId?: (leadId: string) => void;
    parentUrl?: string;
    executingFunction?: boolean;
    functionName?: string;
    trackWidgetClick: (action: string, label: string, category: string) => void;
}

const MemoizedComponent = memo(({
    component,
    config,
    conversationId,
    sendMessage,
    formFields,
    onFormFieldUpdate,
    sidebar,
    gettingFullResponse,
    isLastFormMessage,
    setLeadId,
    parentUrl,
    executingFunction,
    functionName,
    trackWidgetClick
}: MemoizedComponentProps) => {

    const FallbackComponent = ({ component, content }: { component: any, content: any }) => {
        const displayContent = typeof component === 'string' ? component : JSON.stringify(component, null, 2);

        if (content) {
            return (
                <div className={`font-light mt-6 mb-6 rounded-xl ${config ? "" : "bg-blue-100"} text-slate-800 p-4`} style={{ backgroundColor: `${config ? config.end_color : ""}` }}>
                    <code className="whitespace-pre-wrap break-words">{content}</code>
                </div>
            );
        } else {
            return (
                <code className="border p-1 rounded-md bg-blue-100 text-slate-800 break-words">{displayContent}</code>
            );
        }
    };

    switch (component.type) {
        case 'calendly':
            return <Calendly
                url={component.calendly_url}
                key={component.calendly_url}
                gettingFullResponse={gettingFullResponse || false}
                config={config}
            />;
        case 'form':
            return isLastFormMessage ? (
                <Form
                    key={`${component.field_name}-${component.field_type}`}
                    parentUrl={parentUrl}
                    conversationId={conversationId || ''}
                    setLeadId={setLeadId}
                    onSubmit={(fieldName, value) => {
                        sendMessage(`The visitor has filled out the form.`, 'system');
                    }}
                    config={config}
                    gettingFullResponse={gettingFullResponse || false}
                    fields={formFields ? [...Object.values(formFields)] : []}
                    onUpdate={onFormFieldUpdate}
                />
            ) : null;
        case 'form_field':
            return <Form
                key={`${component.field_name}-${component.field_type}`}
                conversationId={conversationId || ''}
                parentUrl={parentUrl}
                setLeadId={setLeadId}
                onSubmit={(fieldName, value) => {
                    sendMessage(`The visitor has filled out the form.`, 'system');
                }}
                config={config}
                gettingFullResponse={gettingFullResponse || false}
                fields={formFields ? [...Object.values(formFields)] : []}
                onUpdate={onFormFieldUpdate}
            />;
        case 'tidycal':
            return <TidyCal
                url={component.tidy_cal_url}
                key={component.tidy_cal_url}
                gettingFullResponse={gettingFullResponse || false}
                config={config}
            />;
        case 'chili_piper':
            return <ChiliPiper
                email={component.email}
                session_id={conversationId || ''}
                key={component.url}
                gettingFullResponse={gettingFullResponse || false}
                config={config}
            />;
        case 'youtube':
            return <YouTube video_id={component.video_id} sidebar={sidebar} />;
        case 'aimdoc_action':
            return <Actions
                actions={component.actions}
                config={config}
            />;
        default:
            if (component.actions) {
                return (
                    <Actions
                        actions={component.actions}
                        config={config}
                    />
                )
            } else {
                return <FallbackComponent
                    component={component.content || component}
                    content={component}
                />;
            }
    }
}, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.component) === JSON.stringify(nextProps.component) &&
        prevProps.formFields === nextProps.formFields &&
        prevProps.gettingFullResponse === nextProps.gettingFullResponse &&
        prevProps.isLastFormMessage === nextProps.isLastFormMessage &&
        prevProps.setLeadId === nextProps.setLeadId &&
        prevProps.parentUrl === nextProps.parentUrl &&
        prevProps.executingFunction === nextProps.executingFunction &&
        prevProps.functionName === nextProps.functionName;
});

const Message: React.FC<MessageProps> = memo(({
    role,
    content,
    created_at,
    leadEmail,
    config,
    scrollToBottom,
    sidebar,
    gettingResponse,
    gettingFullResponse,
    agentName,
    isAgentTyping,
    conversationId,
    sendMessage,
    formFields,
    onFormFieldUpdate,
    setLeadId,
    parentUrl,
    executingFunction,
    functionName,
    trackWidgetClick,
    isLastAssistantMessage
}) => {
    const [logo, setLogo] = useState<string>(aimdocLogo);
    const [showFunctionMessage, setShowFunctionMessage] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const preprocessJson = (jsonString: any) => {
        let correctedString = jsonString.replace(/\\'/g, "'");
        correctedString = correctedString.replace(/,(?=\s*[}\]])/g, "");
        return correctedString;
    }

    const getComponent = useMemo(() => (component: Component) => {
        return (
            <MemoizedComponent
                component={component}
                config={config}
                conversationId={conversationId || ''}
                sendMessage={sendMessage}
                formFields={formFields}
                onFormFieldUpdate={onFormFieldUpdate}
                sidebar={sidebar}
                gettingFullResponse={gettingFullResponse || false}
                setLeadId={setLeadId}
                parentUrl={parentUrl}
                executingFunction={executingFunction}
                functionName={functionName}
                trackWidgetClick={trackWidgetClick}
            />
        );
    }, [config, conversationId, sendMessage, formFields, onFormFieldUpdate, sidebar, gettingFullResponse, setLeadId, parentUrl, executingFunction, functionName, trackWidgetClick]);

    const detectAndRenderUrls = (content: string): string => {
        if (!gettingFullResponse) {
            return content;
        }

        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const markdownUrlPattern = /\[([^\]]+)\]\((https?:\/\/[^\s]+)\)/g;
        const markdownStartPattern = /\[/;

        const lastOpenBracketIndex = content.lastIndexOf('[');

        if (lastOpenBracketIndex !== -1) {
            const substringFromLastBracket = content.slice(lastOpenBracketIndex);
            if (!markdownUrlPattern.test(substringFromLastBracket)) {
                return content.slice(0, lastOpenBracketIndex);
            }
        }

        return content;
    };

    const UlRenderer = (props: any) => (
        <ul className="pl-8 pr-8 list-disc w-full max-w-full space-y-2" style={{ overflowWrap: 'anywhere' }}>
            {props.children}
        </ul>
    );

    const OlRenderer = (props: any) => (
        <ol className="pl-8 pr-8 list-decimal w-full max-w-full space-y-2" style={{ overflowWrap: 'anywhere' }}>
            {props.children}
        </ol>
    );

    const ListItemRenderer = (props: any) => (
        <li className="font-semibold w-full max-w-full text-[12.5px]" style={{ overflowWrap: 'anywhere' }}>
            {props.children}
        </li>
    );

    const ParagraphRenderer = (props: any) => (
        <p className={`${role === 'user' ? '' : 'w-full break-words'}`} style={{ overflowWrap: 'anywhere' }}>
            {props.children}
        </p>
    );

    const URLRenderer = (props: any) => (
        <a
            href={props.href}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWidgetClick('LINK', props.href, 'message_link')}
            className={`text-blue-500 underline break-words w-full max-w-full`}
        >
            {props.children}
        </a>
    );

    const h1Renderer = (props: any) => (
        <h1 className="text-2xl font-bold w-full break-words">{props.children}</h1>
    );

    const h2Renderer = (props: any) => (
        <h2 className="text-xl font-bold w-full break-words">{props.children}</h2>
    );

    const h3Renderer = (props: any) => (
        <h3 className="text-lg font-bold w-full break-words">{props.children}</h3>
    );

    const strongRenderer = (props: any) => (
        <strong className="font-bold">{props.children}</strong>
    );

    const RenderChatComponent = (props: any) => {

        let component;

        try {

            const clean = preprocessJson(props.children);
            component = JSON.parse(clean);

            if (Array.isArray(component)) {
                return (
                    <>
                        {component.map((comp, index) => {
                            return getComponent(comp);
                        })}
                    </>
                );
            } else {
                return getComponent(component);
            }

        } catch (e) {
            // Not a long term solution but making assumption that if not valid JSON the markdown code is SQL
            // const ChatComponent = FallbackComponent;
            // return <ChatComponent component={props.children} content={content} />;
            return <code></code>;
        }

    }

    const markdownRenderers = {
        ul: UlRenderer,
        ol: OlRenderer,
        li: ListItemRenderer,
        p: ParagraphRenderer,
        a: URLRenderer,
        code: RenderChatComponent,
        h1: h1Renderer,
        h2: h2Renderer,
        h3: h3Renderer
    };

    const checkIfMobile = () => {
        return window.innerWidth < 1025;
    };

    return (
        <div className={`${config.font} flex flex-col`}>
            <div
                key={content}
                className={`flex space-x-2 ${role === 'user' ? 'ml-12 justify-end' : ''
                    }`}
            >
                <div
                    className={`text-sm rounded-2xl ${role === 'user'
                        ? 'p-3 text-slate-900 bg-opacity-50 bg-white'
                        : `${(gettingResponse || isAgentTyping) ? 'bg-opacity-100 animate-pulse' : 'bg-slate-50/50 border border-slate-200/70'} ${isAgentTyping ? 'pt-3' : 'pt-2 px-3 pb-3'}`
                        }`}
                    style={{
                        backgroundColor: role === 'user' && config ? `rgba(${hexToRgb(config.color)}, 0.3)` : '',
                        border: role === 'user' && config ? `1px solid rgba(${hexToRgb(config.color)}, 0.0)` : '',
                    }}
                >
                    {role !== 'user' && (
                        <div className="flex items-center mb-2 -ml-1">
                            <div className="flex-shrink-0 items-center justify-center mr-1">
                                {config && config.agent_message_icon_type === 'Icon' && config.agent_message_icon && !agentName && (
                                    <div
                                        className={`${sidebar || checkIfMobile() ? 'w-6 h-6' : 'w-6 h-6'} flex-shrink-0 items-center justify-center rounded-full text-white flex flex-col`}
                                        style={{ backgroundColor: `${config.color}` }}
                                    >
                                        {React.createElement(HeroIconSolid[config.agent_message_icon as keyof typeof HeroIconSolid], {
                                            className: 'w-3 h-3',
                                            fill: 'white',
                                        })}
                                    </div>
                                )}
                                {config && config.agent_message_icon_type === 'Image' && config.message_icon_url && !agentName && (
                                    <img
                                        src={config.message_icon_url}
                                        alt="agent icon"
                                        className={`w-6 h-6 rounded-md`}
                                    />
                                )}
                                {config && agentName && (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 fill-green-500">
                                        <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                    </svg>
                                )}
                                {!config && (
                                    <img
                                        src={logo}
                                        alt="aimdoc logo"
                                        className="h-5 w-5"
                                    />
                                )}
                            </div>
                            <div className="flex items-center">
                                {agentName && !isAgentTyping && (
                                    <span className="font-semibold text-xs text-green-500">{agentName}</span>
                                )}
                                {(gettingResponse || isAgentTyping || executingFunction) && (
                                    <div className="flex ml-1">
                                        {(gettingResponse || executingFunction) && (
                                            <div className="flex space-x-1 items-center">
                                                {/* <div
                                                    className="h-2 w-2 shadow-lg rounded-full bg-white/50"
                                                    style={{
                                                        backgroundColor: `${config.color}`,
                                                        opacity: 0.5,
                                                        animation: 'combinedPulse 1.5s ease-in-out infinite'
                                                    }}
                                                ></div> */}
                                                {executingFunction && (
                                                    <div
                                                        className="text-xs text-slate-700 opacity-100 transition-opacity duration-300 ease-in"
                                                    >
                                                        {functionName}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {isAgentTyping && (
                                            <div className='flex space-x-1 justify-center items-center'>
                                                <div className='h-1.5 w-1.5 shadow-lg rounded-full animate-bounce [animation-delay:-0.3s] bg-green-500/50'></div>
                                                <div className='h-1.5 w-1.5 shadow-lg rounded-full animate-bounce [animation-delay:-0.15s] bg-green-500/50'></div>
                                                <div className='h-1.5 w-1.5 shadow-lg rounded-full animate-bounce bg-green-500/50'></div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="space-y-2">
                        {(!gettingResponse && !isAgentTyping && !executingFunction) && (
                            <Markdown
                                remarkPlugins={[remarkBreaks]}
                                rehypePlugins={[rehypeRaw]}
                                children={detectAndRenderUrls(content)}
                                components={markdownRenderers}
                            ></Markdown>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return prevProps.content === nextProps.content &&
        prevProps.role === nextProps.role &&
        prevProps.gettingResponse === nextProps.gettingResponse &&
        prevProps.gettingFullResponse === nextProps.gettingFullResponse &&
        prevProps.isAgentTyping === nextProps.isAgentTyping &&
        prevProps.formFields === nextProps.formFields &&
        prevProps.parentUrl === nextProps.parentUrl &&
        prevProps.executingFunction === nextProps.executingFunction &&
        prevProps.functionName === nextProps.functionName
});

export default Message;